import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import React from "react";

import { serializeThemeRef } from "./themeChipRef";
import { ThemeSwatchBadge } from "./ThemeSwatchBadge";

export type SerializedThemeMentionNode = Spread<
  { themeId: string; colors: string[] },
  SerializedLexicalNode
>;

/**
 * A custom theme referenced inside the composer, rendered as a swatch chip (see ThemeSwatchBadge).
 * Seeded by the theme selector's Edit action; unlike a block mention it has no remove button —
 * the sentence it sits in ("Update my <theme>: make it …") is meaningless without it.
 */
export class ThemeMentionNode extends DecoratorNode<React.ReactElement> {
  __themeId: string;
  __colors: string[];

  static getType(): string {
    return "theme-mention";
  }

  static clone(node: ThemeMentionNode): ThemeMentionNode {
    return new ThemeMentionNode(node.__themeId, node.__colors, node.__key);
  }

  constructor(themeId: string, colors: string[], key?: NodeKey) {
    super(key);
    this.__themeId = themeId;
    this.__colors = colors;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.style.display = "inline";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute("data-theme-id", this.__themeId);
    element.setAttribute("data-theme-colors", this.__colors.join(","));
    element.textContent = this.getTextContent();
    return { element };
  }

  /** Counterpart to exportDOM, so a copied chip pasted back as HTML stays a chip. */
  static importDOM(): DOMConversionMap | null {
    return {
      span: (element: HTMLElement) => {
        const themeId = element.getAttribute("data-theme-id");
        if (!themeId) {
          return null;
        }
        const colors = element.getAttribute("data-theme-colors");
        return {
          conversion: () => ({
            node: $createThemeMentionNode(themeId, colors ? colors.split(",") : []),
          }),
          priority: 1 as const,
        };
      },
    };
  }

  static importJSON(serializedNode: SerializedThemeMentionNode): ThemeMentionNode {
    return $createThemeMentionNode(serializedNode.themeId, serializedNode.colors);
  }

  exportJSON(): SerializedThemeMentionNode {
    return {
      ...super.exportJSON(),
      type: "theme-mention",
      themeId: this.__themeId,
      colors: this.__colors,
    };
  }

  getTextContent(): string {
    return serializeThemeRef({ themeId: this.__themeId, colors: this.__colors });
  }

  isInline(): boolean {
    return true;
  }

  decorate(): React.ReactElement {
    return <ThemeSwatchBadge colors={this.__colors} />;
  }
}

export function $createThemeMentionNode(themeId: string, colors: string[]): ThemeMentionNode {
  return $applyNodeReplacement(new ThemeMentionNode(themeId, colors));
}

export function $isThemeMentionNode(
  node: LexicalNode | null | undefined
): node is ThemeMentionNode {
  return node instanceof ThemeMentionNode;
}
